/**
 * Utility functions for consistent number and currency formatting
 */

/**
 * Format numbers with commas every 3 digits
 * @param {number|string} number - The number to format
 * @returns {string} Formatted number with commas
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0';
  return Number(number).toLocaleString('en-US');
};

/**
 * Format currency with 3 decimal places and commas
 * @param {number|string} number - The number to format as currency
 * @returns {string} Formatted currency with 2 decimal places
 */
export const threeDecimalformatCurrency = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.000';
  return Number(number).toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  });
};

/**
 * Format currency with 2 decimal places and commas
 * @param {number|string} number - The number to format as currency
 * @returns {string} Formatted currency with 2 decimal places
 */
export const formatCurrency = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return Number(number).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Format percentage with 2 decimal places and commas
 * @param {number|string} number - The number to format as percentage
 * @returns {string} Formatted percentage with 2 decimal places
 */
export const formatPercentage = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return Number(number).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Format large numbers with appropriate suffixes (K, M, B)
 * @param {number|string} number - The number to format
 * @returns {string} Formatted number with suffix
 */
export const formatLargeNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0';
  
  const num = Number(number);
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1) + 'B';
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(1) + 'M';
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(1) + 'K';
  }
  
  return formatNumber(num);
};

export default {
  formatNumber,
  threeDecimalformatCurrency,
  formatCurrency,
  formatPercentage,
  formatLargeNumber
};
